<template>
    <div class="body">
      <img :src="albumImage" alt="">
      <div class="image-wrapper">
        <input type="file" hidden ref="uploadedFile" accept="image/png, image/jpeg" @change="onPhotoUpload">
        <button class="close_modal" @click="onCloseDialog"></button>
  
        <v-dialog :content-class="'photo_clipper_dialog'" v-model="showDialog" width="500" :persistent="false" :retain-focus="false">
          <div class="progress_loader" v-if="uploadImageLoader">
            <v-progress-circular :size="100" :width="3" color="#20C4F5" indeterminate></v-progress-circular>
          </div>
  
          <div class="photo_clipper">
            <button class="dialog-close-btn" @click="onCloseDialog" aria-label="Close dialog">×</button>
            
            <!-- Error overlay only shown for initially invalid images -->
            <div v-if="showInitialSizeError && !uploadImageLoader" class="dimension-error-overlay">
              <div class="error-icon">
                <v-icon large color="#ff4747">mdi-alert-circle</v-icon>
              </div>
              <div class="dimension-error-message">
                Image does not fit criteria, please choose a JPG or PNG photo at least 1,000 pixels in width and length as
                well as less than 10 mb in size.
              </div>
              <div class="current-dimensions">
                Current size: {{ originalWidth }} x {{ originalHeight }}
              </div>
            </div>
            
            <!-- Only show clipper when no initial size error -->
            <template v-else>
              <clipper-fixed
                preview="avatarPreview"
                ref="clipper"
                :ratio="1"
                class="basic lg clipper-fixed"
                :src="clipperAvatar"
                bg-color="transparent"
                :outputSize="null"
                :fixed="true"
                :round="false"
                :grid="false"
                :min-scale="0.75"
                :initial-scale="internalScale"
                :zoomable="true"
                shadow="rgba(0,0,0,0.8)"
                :area="80"
                :handle-zoom-event="onHandleZoom"
                @load="onImgAvaLoad"
                :rotate="0">
                <div slot="placeholder">No image</div>
              </clipper-fixed>
    
              <div class="resolution-container">
                <div class="upload_image_size" :class="{'err': !isCurrentDimensionsValid}">
                  {{ scaleImageWidth }} x {{ scaleImageHeight }}
                </div>
              </div>
              
              <div class="slider-container">
                <span class="zoom-label small">−</span>
                <v-slider
                  v-model="internalScale"
                  :min="minimumAllowedScale"
                  :max="2"
                  :step="0.01"
                  color="#20C4F5"
                  track-color="#7481A6"
                  track-fill-color="#20C4F5"
                  thumb-color="#00D1FF"
                  thumb-size="20"
                  @input="handleRangeInput"
                  hide-details
                >
                </v-slider>
                <span class="zoom-label large">+</span>
              </div>
            </template>
  
            <div class="actions_clipper_photo">
              <button type="button" class="primary_btn small" @click="onChangeImage()" v-ripple>Upload New Photo</button>
              <button 
                type="button" 
                class="clip_photo primary_btn small"
                v-if="!showInitialSizeError && isCurrentDimensionsValid" 
                @click="onClipImage" 
                v-ripple>
                Save
              </button>
              <button v-else type="button" class="clip_photo primary_btn small disabled">Save</button>
            </div>
          </div>
        </v-dialog>
  
        <!-- File Size Limit Error Dialog (Only for MB limit) -->
        <v-dialog v-model="showFileSizeErrorDialog" max-width="600">
            <div class="need_login_dialog style_dialog">
                <h2 class="header">
                    Oops! File Too Large
                </h2>
                <div class="cnt">
                    Please select an image file smaller than {{ FILE_SIZE_LIMIT_MB }}MB.
                </div>
                <div class="wrap_actions center">
                    <button type="button" class="tetriatary_btn" @click="showFileSizeErrorDialog = false" v-ripple>Ok</button>
                </div>
            </div>
        </v-dialog>
  
        <div class="select_cover_album" :class="{'default_img': !displayCoverImage}" @click="onChangeImage">
          <img class="cover_select_img" v-if="displayCoverImage" :src="clipperAvatar" alt="Album cover">
          <span class="select_img">Select</span>
        </div>
  
        <span class="description_">10 MB limit<br> 1000 x 1000 pixels or larger<br> JPG, PNG
          <br>
          <span class="error-msg cover_img" v-if="validationError || !validImage">*Required.</span>
        </span>
      </div>
      <!-- Display general error message (e.g., load errors) -->
      <div class="error-message" v-if="errorMessage && !showFileSizeErrorDialog">{{ errorMessage }}</div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'CoverImageUploader',
    props: {
      albumImage: {
        type: String,
        default: ''
      },
      validationError: {
        type: Boolean,
        default: false
      },
      clipperAvatar: {
        type: String,
        default: ''
      },
      validImage: {
        type: Boolean,
        default: true
      },
      scaleImageWidth: {
        type: Number,
        default: null,
        required: false
      },
      scaleImageHeight: {
        type: Number,
        default: null,
        required: false
      },
      uploadImageLoader: {
        type: Boolean,
        default: false
      },
      clipperDialog: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        internalScale: 1.0,
        localWidth: this.scaleImageWidth,
        localHeight: this.scaleImageHeight,
        dimensionUpdateTimeout: null,
        FILE_SIZE_LIMIT_MB: 10,
        errorMessage: '',
        originalWidth: null,
        originalHeight: null,
        imageHasInitialSizeError: false,
        showFileSizeErrorDialog: false
      }
    },
    computed: {
      showDialog: {
        get() {
          return this.clipperDialog;
        },
        set(value) {
          this.$emit('update:clipper-dialog', value);
        }
      },
      displayCoverImage() {
        return this.clipperAvatar && this.validImage;
      },
      // Check if the original (pre-scaling) image dimensions are valid
      isOriginalDimensionsValid() {
        return this.originalWidth >= 1000 && this.originalHeight >= 1000;
      },
      // Check if the current dimensions (after zoom/scale) are valid
      isCurrentDimensionsValid() {
        // Only validate dimensions if we have an original image that was valid
        if (!this.isOriginalDimensionsValid) {
          return true; // Don't show error if original image wasn't valid to begin with
        }
        return this.scaleImageWidth >= 1000 && this.scaleImageHeight >= 1000;
      },
      // Only show the initial size error if the original image is too small
      showInitialSizeError() {
        return this.imageHasInitialSizeError;
      },
      // Calculate the minimum scale that would keep the dimensions valid
      minimumAllowedScale() {
        if (!this.originalWidth || !this.originalHeight || this.originalWidth < 1000 || this.originalHeight < 1000) {
          return 0.75;
        }
        
        const scaleForMinWidth = 1000 / this.originalWidth;
        const scaleForMinHeight = 1000 / this.originalHeight;
        const minRequiredScale = Math.max(scaleForMinWidth, scaleForMinHeight);
        return Math.max(0.75, minRequiredScale);
      }
    },
    methods: {
      detectAndUpdateDimensions() {
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            // Set both current dimensions and original dimensions
            this.updateLocalDimensions(img.naturalWidth, img.naturalHeight);
            this.setOriginalDimensions(img.naturalWidth, img.naturalHeight);
          }
          URL.revokeObjectURL(img.src);
        };
        
        if (this.clipperAvatar) {
          img.src = this.clipperAvatar;

          // Add backup dimension detection using clipper
          if (this.dimensionUpdateTimeout) {
            clearTimeout(this.dimensionUpdateTimeout);
          }
          
          this.dimensionUpdateTimeout = setTimeout(() => {
            if (this.$refs.clipper) {
              const clipperImg = this.$refs.clipper.$el.querySelector('img');
              if (clipperImg && clipperImg.naturalWidth > 0 && clipperImg.naturalHeight > 0) {
                this.updateLocalDimensions(clipperImg.naturalWidth, clipperImg.naturalHeight);
                this.setOriginalDimensions(clipperImg.naturalWidth, clipperImg.naturalHeight);
              }
            }
          }, 300);
        }
      },

      setOriginalDimensions(width, height) {
        if (width > 0 && height > 0) {
          this.originalWidth = width;
          this.originalHeight = height;
          
          // Determine if this image has an initial size error
          this.imageHasInitialSizeError = width < 1000 || height < 1000;
          
          // If the image is initially valid, adjust the scale to a safe value
          if (!this.imageHasInitialSizeError && this.internalScale < this.minimumAllowedScale) {
            this.internalScale = this.minimumAllowedScale;
          }
        }
      },

      updateLocalDimensions(width, height) {
        // Validate dimensions
        if (!width || !height || width <= 0 || height <= 0) {
          console.warn('Invalid dimensions:', width, 'x', height);
          return;
        }

        // Update local state
        this.localWidth = width;
        this.localHeight = height;

        // Only emit update event if we have valid original dimensions
        if (this.isOriginalDimensionsValid) {
          this.$emit('update-dimensions', {
            width: this.localWidth,
            height: this.localHeight
          });
        }
      },

      handleValidationFailure(type, message) {
        this.errorMessage = '';
        
        if (type === 'size') {
          this.showFileSizeErrorDialog = true;
          this.$emit('update:valid-image', false);
        } else {
          this.errorMessage = message;
          this.$emit('update:valid-image', false);
        }
        this.$emit('update:clipper-dialog', false);
        this.$emit('validation-failed', { type, message });
      },

      onPhotoUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        // Reset errors/dialogs from previous attempts
        this.errorMessage = '';
        this.showFileSizeErrorDialog = false;
        this.imageHasInitialSizeError = false;
        this.$emit('update:valid-image', true);

        // 1. Check file size (MB limit)
        const imageSize = file.size / 1048576;
        if (imageSize > this.FILE_SIZE_LIMIT_MB) {
          this.handleValidationFailure('size', `File size must be less than ${this.FILE_SIZE_LIMIT_MB}MB`);
          event.target.value = '';
          return;
        }

        // 2. Emit event to parent *immediately* to open clipper dialog
        this.$emit('photo-upload', event);

        // 3. Read file for clipper display & check dimensions async
        const reader = new FileReader();
        reader.onload = (e) => {
          // Update clipperAvatar for the dialog display
          this.$emit('update:clipper-avatar', e.target.result);

          // Check dimensions using the loaded data URL
          const img = new Image();
          img.onload = () => {
            this.originalWidth = img.naturalWidth;
            this.originalHeight = img.naturalHeight;
            
            // Set flag for dimension error overlay, DO NOT call handleValidationFailure here
            this.imageHasInitialSizeError = this.originalWidth < 1000 || this.originalHeight < 1000;

            // If dimensions are invalid, mark the overall image state as invalid
            if(this.imageHasInitialSizeError) {
              this.$emit('update:valid-image', false);
            } else {
              // If dimensions ARE valid, ensure the valid-image prop is true
              this.$emit('update:valid-image', true);
            }

            // Update dimensions for display (even if invalid, show current size)
            this.updateLocalDimensions(this.originalWidth, this.originalHeight);
            this.setOriginalDimensions(this.originalWidth, this.originalHeight);
          };
          img.onerror = () => {
            console.error("Failed to load image for dimension check");
            this.handleValidationFailure('load', 'Failed to read image dimensions.');
          }
          img.src = e.target.result;
        };
        reader.onerror = () => {
          console.error("FileReader failed");
          this.handleValidationFailure('read', 'Failed to read file.');
        }
        reader.readAsDataURL(file);

        // 4. Reset file input value after starting processing
        event.target.value = '';
      },
      onCloseDialog() {
        // Reset all states when dialog is closed
        this.errorMessage = '';
        this.imageHasInitialSizeError = false;
        this.originalWidth = null;
        this.originalHeight = null;
        this.localWidth = null;
        this.localHeight = null;
        
        // Reset validation states
        this.$emit('update:valid-image', true);
        this.$emit('update:clipper-avatar', '');
        
        // Close the dialog
        this.$emit('close-dialog');
      },
      onChangeImage() {
        if (this.$refs.uploadedFile) {
          this.$refs.uploadedFile.click();
        } else {
          console.error('File input reference not found in CoverImageUploader');
        }
        
        this.$emit('change-image');
      },
      onImgAvaLoad() {
        if (this.$refs.clipper) {
          const clipperEl = this.$refs.clipper.$el;
          const clipperImg = clipperEl.querySelector('img');
          if (clipperImg && clipperImg.naturalWidth > 0 && clipperImg.naturalHeight > 0) {
            const imgWidth = clipperImg.naturalWidth;
            const imgHeight = clipperImg.naturalHeight;
            
            // Set original dimensions needed for minimumAllowedScale calculation later
            this.setOriginalDimensions(imgWidth, imgHeight);

            // Always calculate the initial scale now
            const viewWidth = clipperEl.clientWidth;
            const viewHeight = clipperEl.clientHeight;

            // Calculate scale to fit image while maintaining aspect ratio
            const scaleToFitWidth = viewWidth / imgWidth;
            const scaleToFitHeight = viewHeight / imgHeight;
            
            // Use the smaller scale to ensure image fits in viewport
            let initialScale = Math.min(scaleToFitWidth, scaleToFitHeight);
            
            // Add a small padding (10%) to ensure image doesn't touch edges
            initialScale *= 0.9;
            
            // Ensure we don't go below minimum allowed scale
            initialScale = Math.max(initialScale, this.minimumAllowedScale);
            
            // Ensure we don't exceed maximum scale
            initialScale = Math.min(initialScale, 2);

            // Apply the calculated scale
            this.$refs.clipper.setWH$.next(initialScale);
            this.internalScale = initialScale;

            // Update dimensions based on this initial scale
            setTimeout(() => {
              if (this.$refs.clipper) {
                const clipperImage = this.$refs.clipper.clip();
                if (clipperImage) {
                  this.updateLocalDimensions(clipperImage.width, clipperImage.height);
                }
              }
            }, 300);
          }
        }
      },
      onHandleZoom(scale) {
        const validScale = parseFloat(scale);
        if (isNaN(validScale)) {
          return;
        }

        // Don't allow scaling below the minimum required for 1000x1000
        // but only if the original image was valid
        const minScale = this.isOriginalDimensionsValid ? this.minimumAllowedScale : 0.75;
        const safeScale = Math.max(minScale, Math.min(validScale, 2));
        
        // Update internal scale directly if changed
        if (safeScale !== this.internalScale) {
          const clipper = this.$refs.clipper;
          if (clipper && clipper.setWH$) {
            clipper.setWH$.next(safeScale);
          }
          this.internalScale = safeScale;
        }
        
        return safeScale;
      },
      onClipImage() {
        if (!this.$refs.clipper) {
          console.error('Clipper not found');
          this.errorMessage = 'Failed to process image. Please try again.';
          return;
        }

        try {
          // Get the image type from the original file
          const uploadedType = this.clipperAvatar.startsWith('data:image/') 
            ? this.clipperAvatar.split(';')[0].split('/')[1]
            : 'jpeg'; // Default to jpeg if type cannot be determined

          // Make sure the clipper has the current scale value
          this.$refs.clipper.setWH$.next(this.internalScale);
          
          // Use the clip method with maxWPixel to ensure proper size and respect zoom area
          const canvas = this.$refs.clipper.clip({
            maxWPixel: 1000
          }).toDataURL(`image/${uploadedType}`, 0.9);

          if (!canvas) {
            throw new Error('Failed to create canvas');
          }

          // Convert to blob
          const blob = this.baseUrlToBlob(canvas);
          
          // Create a File object from the Blob for better compatibility
          const fileName = `album_cover.${uploadedType}`;
          const file = new File([blob], fileName, { type: `image/${uploadedType}` });
          
          // First set the local clipperAvatar to the clipped image
          this.$emit('update:clipper-avatar', canvas);
          
          // Then close the dialog
          this.$emit('update:clipper-dialog', false);
          
          // Finally emit the processed file
          this.$emit('clip-image-complete', file);

        } catch (error) {
          console.error('Error clipping image:', error);
          this.errorMessage = 'Failed to process image. Please try again.';
          setTimeout(() => {
            this.errorMessage = '';
          }, 3000);
        }
      },
      
      baseUrlToBlob(dataUrl) {
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        
        return new Blob([u8arr], { type: mime });
      },
      getClipper() {
        return this.$refs.clipper;
      },
      handleRangeInput(newScale) {
        // Don't allow going below minimum required scale
        if (newScale < this.minimumAllowedScale) {
          newScale = this.minimumAllowedScale;
        }
        
        // Immediately calculate and update dimensions during sliding
        const clipper = this.$refs.clipper;
        if (clipper && clipper.setWH$) {
          clipper.setWH$.next(newScale);
          
          setTimeout(() => {
            const clipperImage = clipper.clip();
            if (clipperImage) {
              this.updateLocalDimensions(clipperImage.width, clipperImage.height);
            }
          }, 50);
          
          this.internalScale = newScale;
        }
      }
    },
    watch: {
      clipperAvatar: {
        immediate: true,
        handler(newValue) {
          if (newValue) {
            // Use nextTick to ensure the clipper element is ready
            this.$nextTick(() => {
              this.detectAndUpdateDimensions();
            });
          }
        }
      },
      // Only watch dimensions props to update local state if they change externally
      scaleImageWidth(newWidth) {
        if (newWidth !== this.localWidth) {
          this.localWidth = newWidth;
        }
      },
      scaleImageHeight(newHeight) {
        if (newHeight !== this.localHeight) {
          this.localHeight = newHeight;
        }
      }
    },
    mounted() {
      this.detectAndUpdateDimensions();
    }
  }
  </script>

<style lang="scss">
/* Import shared clipper styles */
@import "@/assets/scss/components/clipper-styles.scss";
</style>